import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SchoolDetailsModal from '../../components/SchoolDetailsModal';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import {
  ArrowLeft,
  Monitor,
  Wifi,
  Tv,
  Tablet,
  Server,
  TrendingUp,
  Award,
  CheckCircle2,
} from 'lucide-react';
import {
  ComparisonBarChart,
  DistributionPieChart,
  StatCard,
} from '../../components/analytics';
import {
  getTotalComputersICT,
  getIctComputerLabs,
  getEDigiLearningRooms,
  getInteractivePanels,
  getSmartTVs,
  getInternetConnected,
  getTopSchoolsByICT,
  getClassroomsPmShriComparison,
} from '../../services/api';
import { Loader2, RefreshCw } from 'lucide-react';



const AI_BASE_URL = process.env.REACT_APP_AI_API_URL || "";
const AI_API_KEY  = process.env.REACT_APP_AI_API_KEY  || "";

async function fetchAI<T>(path: string): Promise<T> {
  const res = await fetch(`${AI_BASE_URL}${path}`, { headers: { "X-API-Key": AI_API_KEY } });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json() as Promise<T>;
}

const INTERNET_TYPE_LABEL: Record<number, string> = {
  1: "BroadBand", 2: "LeaseLine", 3: "Optical Fibre",
  4: "VSAT", 5: "Others", 6: "AirFiber",
};

export default function ICTProfileAnalysis() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'computers' | 'labs' | 'eClassrooms' | 'smartTVs' | 'internet' | 'gaps'>('computers');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [topSchools, setTopSchools] = useState<any[]>([]);
  const [bottomSchools, setBottomSchools] = useState<any[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [gapsData, setGapsData] = useState<any[]>([]);
  const [internetTypes, setInternetTypes] = useState<any[]>([]);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<{
    schoolID: number;
    schoolName: string;
    infraData?: any;
    tabLabel?: string;
  } | null>(null);

  const TAB_WISE_MAP: Record<string, string> = {
    computers: 'computers',
    labs: 'eclassrooms',
    eClassrooms: 'eclassrooms',
    smartTVs: 'displaydevices',
    internet: 'Internet',
    // 'gaps' intentionally omitted — fetches from AI backend, not VSK API
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [computers, labsData, eLearningRooms, panels, smartTVs, internet, comparison] = await Promise.all([
        getTotalComputersICT(),
        getIctComputerLabs(),
        getEDigiLearningRooms(),
        getInteractivePanels(),
        getSmartTVs(),
        getInternetConnected(),
        getClassroomsPmShriComparison(),
      ]);

      setData({
        summary: {
          totalComputers: computers?.total || 0,
          // Computer Labs: use comparison API (same source as Infrastructure dashboard = 2,504)
          totalLabs: comparison?.Total?.ICT_Profile?.Labs?.Total_Computer_Labs || labsData?.summary?.totalLabs || 0,
          // Smart Classrooms: typeA+typeB+typeD+panels_in_class+panels_outside (matches dashboard = ~40,235)
          smartClassrooms: comparison?.Total?.SmartClassrooms || 0,
          smartTVs: (smartTVs?.PMSHRI?.totalSmartTvs || 0) + (smartTVs?.nonPMSHRI?.totalSmartTvs || 0),
          internetConnected: (internet?.PMSHRI?.totalInternetConnected || 0) + (internet?.nonPMSHRI?.totalInternetConnected || 0),
        },
        computersBreakdown: computers?.eachLabTypeBreakdown || [],
        labsBreakdown: labsData?.summary || {},
        eClassroomsBreakdown: eLearningRooms?.breakdown || [],
        labs: {
          pmshri: {
            total: eLearningRooms?.PMSHRI?.totalRooms || 0,
            lanConnectivity: Math.floor((eLearningRooms?.PMSHRI?.totalRooms || 0) * 0.85),
            internetConnectivity: Math.floor((eLearningRooms?.PMSHRI?.totalRooms || 0) * 0.75),
          },
          nonPmshri: {
            total: eLearningRooms?.nonPMSHRI?.totalRooms || 0,
            lanConnectivity: Math.floor((eLearningRooms?.nonPMSHRI?.totalRooms || 0) * 0.65),
            internetConnectivity: Math.floor((eLearningRooms?.nonPMSHRI?.totalRooms || 0) * 0.55),
          },
        },
        eClassrooms: {
          pmshri: {
            typeA: Math.floor((panels?.PMSHRI?.totalPanels || 0) * 0.3),
            typeB: Math.floor((panels?.PMSHRI?.totalPanels || 0) * 0.15),
            typeCClassroom: Math.floor((panels?.PMSHRI?.totalPanels || 0) * 0.4),
            typeCOther: Math.floor((panels?.PMSHRI?.totalPanels || 0) * 0.1),
            typeD: Math.floor((panels?.PMSHRI?.totalPanels || 0) * 0.05),
          },
          nonPmshri: {
            typeA: Math.floor((panels?.nonPMSHRI?.totalPanels || 0) * 0.2),
            typeB: Math.floor((panels?.nonPMSHRI?.totalPanels || 0) * 0.1),
            typeCClassroom: Math.floor((panels?.nonPMSHRI?.totalPanels || 0) * 0.45),
            typeCOther: Math.floor((panels?.nonPMSHRI?.totalPanels || 0) * 0.15),
            typeD: Math.floor((panels?.nonPMSHRI?.totalPanels || 0) * 0.1),
          },
        },
        smartTVs: {
          pmshri: smartTVs?.PMSHRI?.totalSmartTvs || 0,
          nonPmshri: smartTVs?.nonPMSHRI?.totalSmartTvs || 0,
        },
        internet: {
          pmshri: internet?.PMSHRI?.totalInternetConnected || 0,
          nonPmshri: internet?.nonPMSHRI?.totalInternetConnected || 0,
        },
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load ICT data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchSchools = async () => {
      if (!TAB_WISE_MAP[activeTab]) return;
      setSchoolsLoading(true);
      try {
        const [top, bottom] = await Promise.all([
          getTopSchoolsByICT(TAB_WISE_MAP[activeTab], 'desc'),
          getTopSchoolsByICT(TAB_WISE_MAP[activeTab], 'asc'),
        ]);
        setTopSchools([...(top?.PMSHRI || []), ...(top?.nonPMSHRI || [])]);
        setBottomSchools([...(bottom?.PMSHRI || []), ...(bottom?.nonPMSHRI || [])]);
      } catch {
        setTopSchools([]);
        setBottomSchools([]);
      } finally {
        setSchoolsLoading(false);
      }
    };
    fetchSchools();
  }, [activeTab]);

  // Fetch gap analysis from AI backend when gaps tab is active
  useEffect(() => {
    if (activeTab !== 'gaps') return;
    setGapsLoading(true);
    Promise.all([
      fetchAI<any[]>('/api/v1/ict/gaps'),
      fetchAI<any[]>('/api/v1/ict/internet-types'),
    ])
      .then(([gaps, types]) => {
        setGapsData(gaps);
        setInternetTypes(types);
      })
      .catch(() => {})
      .finally(() => setGapsLoading(false));
  }, [activeTab]);

  const handleCSVDownload = async () => {
    const res = await fetch(`${AI_BASE_URL}/api/v1/ict/report.csv`, {
      headers: { "X-API-Key": AI_API_KEY },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "kvs_ict_report.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="w-full max-w-7xl mx-auto px-4 pt-4">
          <Breadcrumb />
        </div>
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading ICT data...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="w-full max-w-7xl mx-auto px-4 pt-4">
          <Breadcrumb />
        </div>
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) return null;

  const tabs = [
    { id: 'computers', label: 'Computers by Location', icon: Monitor },
    { id: 'labs', label: 'Computer Labs', icon: Server },
    { id: 'eClassrooms', label: 'E-Classrooms', icon: Tv },
    { id: 'smartTVs', label: 'Smart TVs', icon: Tablet },
    { id: 'internet', label: 'Internet Connectivity', icon: Wifi },
    { id: 'gaps', label: 'Smart Classroom Gaps', icon: TrendingUp },
  ];

  const addPercentage = (items: { category: string; pmshriCount: number; nonPmshriCount: number; total: number }[]) =>
    items.map(item => ({
      ...item,
      pmshriPercentage: item.total > 0 ? (item.pmshriCount / item.total) * 100 : 0
    }));

  const getComparisonData = () => {
    if (!data) return [];
    
    switch (activeTab) {
      case 'computers':
        // Use actual breakdown from API
        return addPercentage(
          data.computersBreakdown.map((item: any) => ({
            category: item.label,
            pmshriCount: item.PMSHRI || 0,
            nonPmshriCount: item.nonPMSHRI || 0,
            total: item.total || 0,
          }))
        );
      case 'labs': {
        // labsBreakdown = labsData.summary: { totalLabs, totalLabsWithLan, totalLabsWithInternet, pmshri:{}, nonPmshri:{} }
        const lb = data?.labsBreakdown || {};
        const lbPm    = lb.pmshri    || {};
        const lbNonPm = lb.nonPmshri || {};
        const labRows = [
          { key: 'totalLabs',            label: 'Total ICT Computer Labs' },
          { key: 'totalLabsWithLan',     label: 'Labs with LAN' },
          { key: 'totalLabsWithInternet', label: 'Labs with Internet' },
        ];
        return addPercentage(labRows.map(({ key, label }) => ({
          category:      label,
          pmshriCount:   lbPm[key]    || 0,
          nonPmshriCount: lbNonPm[key] || 0,
          total:          lb[key]      || 0,
        })));
      }
      case 'eClassrooms':
        return addPercentage(
          data.eClassroomsBreakdown.map((item: any) => ({
            category: item.label,
            pmshriCount: item.PMSHRI || 0,
            nonPmshriCount: item.nonPMSHRI || 0,
            total: item.total || 0,
          }))
        );
      case 'smartTVs':
        return addPercentage([
          { category: 'Smart TVs', pmshriCount: data?.smartTVs?.pmshri || 0, nonPmshriCount: data?.smartTVs?.nonPmshri || 0, total: (data?.smartTVs?.pmshri || 0) + (data?.smartTVs?.nonPmshri || 0) },
        ]);
      case 'internet':
        return addPercentage([
          { category: 'Internet Connectivity', pmshriCount: data?.internet?.pmshri || 0, nonPmshriCount: data?.internet?.nonPmshri || 0, total: (data?.internet?.pmshri || 0) + (data?.internet?.nonPmshri || 0) },
        ]);
      default:
        return [];
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <div className="w-full max-w-7xl mx-auto px-4 pt-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] mb-2 flex items-center gap-2">
              <Monitor className="w-7 h-7" />
              ICT Profile Analysis
            </h1>
            <p className="text-gray-600">
              Computers by location, labs, e-classrooms (Types A-D), smart TVs, and internet connectivity
            </p>
          </div>
          <button
            onClick={() => navigate('/kvs-samagam/analytics')}
            className="mt-2 md:mt-0 flex items-center gap-2 px-4 py-2 border border-purple-700 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analytics
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="Total Computers"
            value={(data?.summary?.totalComputers ?? 0).toLocaleString()}
            icon={<Monitor className="w-6 h-6" />}
            color="blue"
            subtitle="All computer devices"
          />
          <StatCard
            title="Computer Labs"
            value={(data?.summary?.totalLabs ?? 0).toLocaleString()}
            icon={<Server className="w-6 h-6" />}
            color="purple"
            subtitle="Dedicated labs"
          />
          <StatCard
            title="Smart Classrooms"
            value={(data?.summary?.smartClassrooms ?? 0).toLocaleString()}
            icon={<Tablet className="w-6 h-6" />}
            color="green"
            subtitle="All e-classroom types"
          />
          <StatCard
            title="Smart TVs"
            value={(data?.summary?.smartTVs ?? 0).toLocaleString()}
            icon={<Tv className="w-6 h-6" />}
            color="orange"
            subtitle="Digital displays"
          />
          <StatCard
            title="Internet Connected"
            value={(data?.summary?.internetConnected ?? 0).toLocaleString()}
            icon={<Wifi className="w-6 h-6" />}
            color="teal"
            subtitle="Schools with internet"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'text-purple-700 border-purple-600 bg-purple-50'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* ── Gap Analysis (AI backend) ──────────────────────────────────────── */}
        {activeTab === 'gaps' && (
          <div className="space-y-6 mb-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Regions sorted by schools without smart classroom — highest gap first.
              </p>
              <button
                onClick={handleCSVDownload}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-300 bg-emerald-50
                  text-emerald-700 text-sm font-medium hover:bg-emerald-100 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                </svg>
                Download ICT Report CSV
              </button>
            </div>

            {gapsLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
              </div>
            ) : (
              <>
                {/* Gap table */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-800">Smart Classroom Coverage by Region</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Regional Office</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-600">Total KVs</th>
                          <th className="text-center py-3 px-4 font-semibold text-green-600">Have Smart Class</th>
                          <th className="text-center py-3 px-4 font-semibold text-red-500">No Smart Class</th>
                          <th className="text-center py-3 px-4 font-semibold text-purple-600">Coverage</th>
                          <th className="px-4 py-3 font-semibold text-gray-600">Progress</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {gapsData.map((g: any) => (
                          <tr key={g.roID} className="hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-4 font-medium text-gray-800">{g.roName}</td>
                            <td className="py-3 px-4 text-center text-gray-600">{g.totalSchools}</td>
                            <td className="py-3 px-4 text-center font-semibold text-green-600">{g.withSmart}</td>
                            <td className="py-3 px-4 text-center font-semibold text-red-500">{g.withoutSmart}</td>
                            <td className="py-3 px-4 text-center font-bold text-purple-600">{g.smartCoveragePct}%</td>
                            <td className="py-3 px-4 min-w-32">
                              <div className="w-full bg-gray-100 rounded-full h-2">
                                <div
                                  className="h-2 rounded-full bg-green-400"
                                  style={{ width: `${Math.min(g.smartCoveragePct, 100)}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Internet type breakdown */}
                {internetTypes.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-base font-semibold text-gray-800">Internet Connectivity Type Distribution</h3>
                    </div>
                    <div className="p-6 flex flex-wrap gap-4">
                      {internetTypes.map((t: any) => (
                        <div key={t.internetType} className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                          <div className="w-2 h-8 rounded-full bg-purple-400" />
                          <div>
                            <p className="text-sm font-semibold text-gray-800">
                              {INTERNET_TYPE_LABEL[t.internetType] || t.typeName}
                            </p>
                            <p className="text-lg font-bold text-purple-700">{t.schools.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">schools</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Charts + table + schools — hidden when gaps tab is active */}
        {activeTab !== 'gaps' && (<>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ComparisonBarChart
            data={getComparisonData()}
            title={`${tabs.find(t => t.id === activeTab)?.label} - PM-SHRI vs Non PM-SHRI`}
            xAxisLabel="Category"
          />
          <DistributionPieChart
            data={getComparisonData().map((d: any) => ({ name: d.category, value: d.total }))}
            title={`${tabs.find(t => t.id === activeTab)?.label} Distribution`}
          />
        </div>

        {/* Detailed Stats Table */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {tabs.find(t => t.id === activeTab)?.label} Details
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Category</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-green-600">PM-SHRI</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-blue-600">Non PM-SHRI</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-purple-600">Total</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">PM-SHRI %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {getComparisonData().map((row: any) => (
                  <tr key={row.category} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-700 font-medium">{row.category}</td>
                    <td className="py-3 px-4 text-sm text-center text-green-600 font-medium">{(row.pmshriCount ?? 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center text-blue-600 font-medium">{(row.nonPmshriCount ?? 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center text-purple-600 font-bold">{(row.total ?? 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        {row.total > 0 ? ((row.pmshriCount / row.total) * 100).toFixed(1) : '0.0'}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Schools Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Schools */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Award className="w-5 h-5 text-blue-600" />
                Top Schools — {tabs.find(t => t.id === activeTab)?.label}
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {schoolsLoading ? (
                <div className="px-6 py-12 text-center text-gray-400">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p>Loading...</p>
                </div>
              ) : topSchools.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-400">
                  <Award className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No data available</p>
                </div>
              ) : (
                topSchools.map((item: any, index: number) => (
                  <div key={item.school?.schoolID || index} className="px-6 py-4 hover:bg-gray-50 cursor-pointer" onClick={() => item.school?.schoolID && setSelectedSchool({ schoolID: item.school.schoolID, schoolName: item.school.schoolName || 'N/A', infraData: { ...item.data, schoolID: item.school.schoolID, schoolName: item.school.schoolName, state: item.school.location, score: item.score }, tabLabel: tabs.find(t => t.id === activeTab)?.label })}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-600' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-50 text-gray-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-gray-800 text-sm">{item.school?.schoolName || 'N/A'}</h4>
                            {item.school?.status === 'PM-SHRI' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">PM-SHRI</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{item.school?.location || 'N/A'}</p>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {item.data?.totalEClassrooms != null && (
                              <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded">{item.data.totalEClassrooms} E-Class</span>
                            )}
                            {item.data?.touchPanels != null && (
                              <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{item.data.touchPanels} Panels</span>
                            )}
                            {item.data?.smartTvs != null && (
                              <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded">{item.data.smartTvs} Smart TVs</span>
                            )}
                            {item.data?.totalDisplayDevices != null && (
                              <span className="text-xs bg-teal-50 text-teal-600 px-2 py-0.5 rounded">{item.data.totalDisplayDevices} Displays</span>
                            )}
                            {item.data?.internetSpeed && (
                              <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded">{item.data.internetSpeed}</span>
                            )}
                            {item.data?.internetType && (
                              <span className="text-xs bg-cyan-50 text-cyan-600 px-2 py-0.5 rounded">{item.data.internetType}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <div className="text-xl font-bold text-purple-600">{item.score ?? 0}</div>
                        <div className="text-xs text-gray-500">{item.label || 'Score'}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Bottom Schools (Needs Attention) */}
          <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-red-500" />
                Needs Attention — {tabs.find(t => t.id === activeTab)?.label}
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {schoolsLoading ? (
                <div className="px-6 py-12 text-center text-gray-400">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p>Loading...</p>
                </div>
              ) : bottomSchools.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-400">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No data available</p>
                </div>
              ) : (
                bottomSchools.map((item: any, index: number) => (
                  <div key={item.school?.schoolID || index} className="px-6 py-4 hover:bg-red-50 cursor-pointer" onClick={() => item.school?.schoolID && setSelectedSchool({ schoolID: item.school.schoolID, schoolName: item.school.schoolName || 'N/A', infraData: { ...item.data, schoolID: item.school.schoolID, schoolName: item.school.schoolName, state: item.school.location, score: item.score }, tabLabel: tabs.find(t => t.id === activeTab)?.label })}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 bg-red-50 text-red-500">
                          {index + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-gray-800 text-sm">{item.school?.schoolName || 'N/A'}</h4>
                            {item.school?.status === 'PM-SHRI' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">PM-SHRI</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{item.school?.location || 'N/A'}</p>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {item.data?.totalEClassrooms != null && (
                              <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded">{item.data.totalEClassrooms} E-Class</span>
                            )}
                            {item.data?.touchPanels != null && (
                              <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded">{item.data.touchPanels} Panels</span>
                            )}
                            {item.data?.smartTvs != null && (
                              <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded">{item.data.smartTvs} Smart TVs</span>
                            )}
                            {item.data?.totalDisplayDevices != null && (
                              <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded">{item.data.totalDisplayDevices} Displays</span>
                            )}
                            {item.data?.internetSpeed && (
                              <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded">{item.data.internetSpeed}</span>
                            )}
                            {item.data?.internetType && (
                              <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded">{item.data.internetType}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <div className="text-xl font-bold text-red-500">{item.score ?? 0}</div>
                        <div className="text-xs text-gray-500">{item.label || 'Score'}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        </>)}

        {/* Detailed Analysis Reports Section */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure', path: '/kvs-samagam/analytics/infrastructure', icon: '🏫', color: 'blue' },
              { name: 'Enrollment', path: '/kvs-samagam/analytics/enrollment', icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile', path: '/kvs-samagam/analytics/ict-profile', icon: '💻', color: 'purple' },
              { name: 'Library Profile', path: '/kvs-samagam/analytics/library-profile', icon: '📚', color: 'orange' },
              { name: 'CWSN Profile', path: '/kvs-samagam/analytics/cwsn-profile', icon: '♿', color: 'pink' },
              { name: 'NCC Profile', path: '/kvs-samagam/analytics/ncc-profile', icon: '🎖️', color: 'cyan' },
              { name: 'Sports', path: '/kvs-samagam/analytics/sports', icon: '🏆', color: 'yellow' },
              { name: 'Green Building', path: '/kvs-samagam/analytics/green-building', icon: '🌿', color: 'teal' },
              { name: 'Safety', path: '/kvs-samagam/analytics/safety', icon: '🛡️', color: 'red' },
              { name: 'Special Labs', path: '/kvs-samagam/analytics/special-labs', icon: '🔬', color: 'indigo' },
              { name: 'Water Profile', path: '/kvs-samagam/analytics/miscellaneous', icon: '💧', color: 'teal' },
            ].map((item) => {
              const isCurrentPage = item.name === 'ICT Profile';
              return (
                <button
                  key={item.name}
                  onClick={() => !isCurrentPage && navigate(item.path)}
                  disabled={isCurrentPage}
                  className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center transition-all ${
                    isCurrentPage 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:shadow-lg cursor-pointer hover:scale-105'
                  }`}
                >
                  <span className="text-3xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />

      {selectedSchool && (
        <SchoolDetailsModal
          schoolID={selectedSchool.schoolID}
          onClose={() => setSelectedSchool(null)}
          schoolKPIData={selectedSchool.infraData}
          selectedCategory={selectedSchool.tabLabel}
        />
      )}
    </div>
  );
}
